import React, { useState } from "react";
import axios from "axios";
import { Mail, Phone, MapPin } from "lucide-react";
import { siteInfo } from "../mock";
import { useToast } from "../hooks/use-toast";
import useSeo from "../hooks/useSeo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Contact = () => {
  useSeo({
    title: "Contact",
    description:
      "Get in touch with Astoria Solutions for research, finance or auction advisory. Reach us by phone, email, or the contact form.",
    path: "/contact",
  });
  const { toast } = useToast();
  const [form, setForm] = useState({ name: "", mobile: "", email: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.mobile || !form.message) {
      toast({ title: "Missing fields", description: "Please fill required fields." });
      return;
    }
    setSubmitting(true);
    try {
      const payload = { ...form };
      if (!payload.email) delete payload.email;
      await axios.post(`${API}/contact`, payload);
      toast({ title: "Message sent \u2713", description: "We'll get back to you shortly." });
      setForm({ name: "", mobile: "", email: "", message: "" });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast({
        title: "Could not send",
        description: typeof detail === "string" ? detail : "Please try again in a moment.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <section className="bg-black text-white">
        <div className="max-w-7xl mx-auto px-6 py-20 text-center">
          <h1 className="text-4xl md:text-6xl font-extrabold">Get In Touch</h1>
          <p className="mt-4 text-teal-400">We'd love to hear about your next project.</p>
        </div>
      </section>

      <section className="bg-white py-16">
        <div className="max-w-6xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-2 gap-10">
          <form onSubmit={handleSubmit} className="bg-neutral-50 rounded-2xl p-8 border border-neutral-100 shadow-sm">
            <div className="space-y-5">
              <div>
                <label className="text-sm font-semibold text-neutral-800">Name *</label>
                <input
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  className="mt-1 w-full rounded-lg border border-neutral-300 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-teal-400 focus:border-teal-400"
                  placeholder="Your name"
                />
              </div>
              <div>
                <label className="text-sm font-semibold text-neutral-800">Mobile No. *</label>
                <input
                  name="mobile"
                  value={form.mobile}
                  onChange={handleChange}
                  className="mt-1 w-full rounded-lg border border-neutral-300 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-teal-400 focus:border-teal-400"
                  placeholder="+91 98xxxxxxxx"
                />
              </div>
              <div>
                <label className="text-sm font-semibold text-neutral-800">Email</label>
                <input
                  name="email"
                  type="email"
                  value={form.email}
                  onChange={handleChange}
                  className="mt-1 w-full rounded-lg border border-neutral-300 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-teal-400 focus:border-teal-400"
                  placeholder="you@company.com"
                />
              </div>
              <div>
                <label className="text-sm font-semibold text-neutral-800">Message *</label>
                <textarea
                  name="message"
                  value={form.message}
                  onChange={handleChange}
                  rows={5}
                  className="mt-1 w-full rounded-lg border border-neutral-300 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-teal-400 focus:border-teal-400"
                  placeholder="Tell us about your project"
                />
              </div>
              <button
                type="submit"
                disabled={submitting}
                className="inline-flex items-center bg-teal-500 hover:bg-teal-600 text-white font-semibold px-8 py-3 rounded-full transition-colors disabled:opacity-60"
              >
                {submitting ? "Sending..." : "Send"}
              </button>
            </div>
          </form>

          <div className="space-y-6">
            <div className="bg-neutral-900 text-white rounded-2xl p-8">
              <h3 className="text-2xl font-bold">{siteInfo.address.line1}</h3>
              <div className="mt-4 space-y-1 text-neutral-300">
                <p>{siteInfo.address.line2}</p>
                <p>{siteInfo.address.line3}</p>
                <p>{siteInfo.address.line4}</p>
                <p>{siteInfo.address.line5}</p>
              </div>
              <div className="mt-6 space-y-3">
                <p className="flex items-center gap-3"><Phone className="w-4 h-4 text-teal-400" /> {siteInfo.phone}</p>
                <p className="flex items-center gap-3"><Mail className="w-4 h-4 text-teal-400" /> <a className="underline" href={`mailto:${siteInfo.email}`}>{siteInfo.email}</a></p>
                <p className="flex items-center gap-3"><MapPin className="w-4 h-4 text-teal-400" /> Newtown, West Bengal, India</p>
              </div>
            </div>

            <div className="rounded-2xl overflow-hidden border border-neutral-200 shadow-sm">
              <iframe
                title="Astoria Map"
                className="w-full h-64"
                src="https://www.openstreetmap.org/export/embed.html?bbox=88.4600%2C22.5700%2C88.5000%2C22.6000&layer=mapnik&marker=22.5850%2C88.4790"
              />
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Contact;
